/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React, { useState, useRef } from 'react';
import { Modal, Form, Button, Row, Col, Badge } from 'react-bootstrap';
import {
  PlusIcon,
  UserGroupIcon,
  ClipboardCheckIcon,
  DocumentAddIcon,
  UserAddIcon,
  PencilAltIcon,
} from '@heroicons/react/solid';
import { TweetyWidget } from '@vlinder-web/tweety-widget-react';
import { KanbanAvatar } from './components';

export * from './components';

export interface ISupplyChainStepModalProps {
  show?: boolean;
  onHide?(): any;
  backdrop?: 'static' | boolean;
  centered?: boolean;
  fullScreen?:
    | true
    | 'sm-down'
    | 'md-down'
    | 'lg-down'
    | 'xl-down'
    | 'xxl-down';
  isVerticallyCentered?: boolean;
  widgetProps: any;
  size?: 'xm' | 'lg' | 'xl';
  submitButtonTitle?: string;
  scrollable?: boolean;
  onSubmit?(payload: any): void;
  onEditMembersCB?(payload?: any): any;
  onEditComplianceCB?(payload?: any): any;
  onEditSupplyChainStepFormCB?(payload?: any): any;
  onEditRecipientsCB?(payload?: any): any;
  onUpdateTraceStepCB?(payload?: any): any;
}

export const SupplyChainStepModal = ({
  show,
  onHide,
  backdrop,
  fullScreen,
  centered,
  isVerticallyCentered,
  widgetProps,
  size,
  submitButtonTitle,
  onSubmit,
  scrollable,
  onEditMembersCB,
  onEditComplianceCB,
  onEditRecipientsCB,
  onEditSupplyChainStepFormCB,
  onUpdateTraceStepCB,
}: ISupplyChainStepModalProps) => {
  const [showModal, setShowModal] = useState(show);
  const {
    id: cardId,
    index,
    subtitle: description,
    listId,
    members = [],
    complianceProof = [],
    recipients = [],
    supplyChainStepOutput,
    traceStepTemplate = [],
  } = widgetProps;
  const [title, setTitle] = useState(widgetProps.label ?? '');
  const [comment, setComment] = useState('');
  const [isTitleEditable, setIsTitleEditable] = useState(false);

  const modalRef = useRef<any>();

  const toggleIsTitleEditable = () => {
    setIsTitleEditable(!isTitleEditable);
  };

  const onHideCB = () => {
    setShowModal(false);
    onHide && onHide();
  };
  const props: any = {};
  if (isVerticallyCentered) {
    props['aria-labelledby'] = 'contained-modal-title-vcenter';
  }
  if (size) {
    props.size = size;
  }

  const onChange = () => {
    const payload = { listId, cardId, title };

    if (title !== props.title) {
      widgetProps.onChange && widgetProps.onChange(payload);
    }

    toggleIsTitleEditable();
  };

  const onEditMembers = () => {
    onEditMembersCB && onEditMembersCB(widgetProps);
  };

  const onEditCompliance = () => {
    onEditComplianceCB && onEditComplianceCB(widgetProps);
  };

  const onEditRecipients = () => {
    onEditRecipientsCB && onEditRecipientsCB(widgetProps);
  };

  const onUpdateTraceStep = () => {
    onUpdateTraceStepCB &&
      onUpdateTraceStepCB({
        complianceProof: complianceProof,
        suppliers: members,
        recipients: recipients,
      });
    setShowModal(false);
  };

  const onEditSupplyStepForm = () => {
    onEditSupplyChainStepFormCB && onEditSupplyChainStepFormCB(widgetProps);
  };

  return (
    <Modal
      as={Modal.Dialog}
      centered={centered}
      show={showModal}
      onHide={onHideCB}
      backdrop={backdrop}
      fullscreen={fullScreen}
      scrollable={scrollable}
      {...props}
    >
      <Form className="modal-content p-lg-3">
        <Modal.Header className="align-items-start border-bottom">
          <div className="d-block">
            {isTitleEditable ? (
              <Form.Group id="title" className="mb-3">
                <Form.Control
                  required
                  autoFocus
                  value={title}
                  className="text-gray-900 fs-5 fw-bold border-0 px-1 py-0 m-0"
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={onChange}
                />
              </Form.Group>
            ) : (
              <h5
                className="text-gray-900 fs-5 fw-bold py-1 ps-1 mb-3"
                onClick={toggleIsTitleEditable}
              >
                {title}
              </h5>
            )}
            {description && (
              <p className="text-gray-900 py-1 ps-1 mb-3">{description}</p>
            )}
            {supplyChainStepOutput && (
              <div className="d-block me-3 mb-3">
                <div className="d-flex align-items-center">
                  <Badge
                    text="white"
                    className="rounded py-2 px-3"
                    bg={'primary'}
                  >
                    {supplyChainStepOutput}
                  </Badge>
                </div>
              </div>
            )}
            <div className="d-flex">
              <div className="d-block me-3 me-sm-4">
                <h5 className="fs-6 fw-bold text-gray-500">Suppliers</h5>
                <div className="d-flex align-items-center">
                  {members.map((m: any) => (
                    <KanbanAvatar key={`kanban-member-avatar-${m.id}`} {...m} />
                  ))}
                  <Button
                    variant="gray-200"
                    size="sm"
                    className="d-inline-flex align-items-center px-3 ms-1"
                    onClick={onEditMembers}
                  >
                    <PlusIcon className="icon icon-xs" />
                  </Button>
                </div>
              </div>
              <div className="d-block me-3">
                <h5 className="fs-6 fw-bold text-gray-500">Compliance Proof</h5>
                <div className="d-flex align-items-center">
                  {complianceProof.map((m: any) => (
                    <KanbanAvatar
                      key={`kanban-compliance-avatar-${m.id}`}
                      {...m}
                    />
                  ))}
                  <Button
                    variant="gray-200"
                    size="sm"
                    className="d-inline-flex align-items-center px-3 ms-1"
                    onClick={onEditCompliance}
                  >
                    <PlusIcon className="icon icon-xs" />
                  </Button>
                </div>
              </div>
              <div className="d-block me-3">
                <h5 className="fs-6 fw-bold text-gray-500">Recipients</h5>
                <div className="d-flex align-items-center">
                  {recipients.map((m: any) => (
                    <KanbanAvatar
                      key={`kanban-compliance-avatar-${m.id}`}
                      {...m}
                    />
                  ))}
                  <Button
                    variant="gray-200"
                    size="sm"
                    className="d-inline-flex align-items-center px-3 ms-1"
                    onClick={onEditRecipients}
                  >
                    <PlusIcon className="icon icon-xs" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <Button variant="close" onClick={onHideCB} />
        </Modal.Header>
        <Modal.Body className="py-4">
          <Row>
            <Col xs={12} lg={9}>
              <h5 className="fs-6 fw-bold text-gray-500">
                Trace Step Template
              </h5>
              <Col xs={8}>
                <TweetyWidget
                  fields={traceStepTemplate}
                  submitButtonLabel={'Preview'}
                />
              </Col>
            </Col>
            <Col xs={12} lg={3}>
              <div className="d-grid gap-2">
                <Button
                  variant="gray-200"
                  size="sm"
                  className="d-inline-flex align-items-center rounded py-2 ps-3 text-start"
                  onClick={onEditMembers}
                >
                  <UserGroupIcon className="icon icon-xs text-gray-500 me-2" />
                  Suppliers
                </Button>
                <Button
                  variant="gray-200"
                  size="sm"
                  className="d-inline-flex align-items-center rounded py-2 ps-3 text-start"
                  onClick={onEditCompliance}
                >
                  <ClipboardCheckIcon className="icon icon-xs text-gray-500 me-2" />
                  Compliance Proofs
                </Button>
                <Button
                  variant="gray-200"
                  size="sm"
                  className="d-inline-flex align-items-center rounded py-2 ps-3 text-start"
                  onClick={onEditRecipients}
                >
                  <UserAddIcon className="icon icon-xs text-gray-500 me-2" />
                  Recipients
                </Button>
                <Button
                  variant="gray-200"
                  size="sm"
                  className="d-inline-flex align-items-center rounded py-2 ps-3 text-start"
                  onClick={onEditSupplyStepForm}
                >
                  <DocumentAddIcon className="icon icon-xs text-gray-500 me-2" />
                  Trace Data Template
                </Button>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="justify-content-start border-top">
          <Button
            variant="gray-800"
            className="me-2 text-start"
            onClick={onUpdateTraceStep}
          >
            <PencilAltIcon className="icon icon-xs me-2" />
            Update Trace Step
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

SupplyChainStepModal.defaultProps = {
  show: false,
  backdrop: true,
  centered: true,
  isVerticallyCentered: false,
  submitButtonTitle: 'Submit',
  scrollable: true,
  fullScreen: undefined,
};
